import React, { createContext, useContext, useState, useEffect } from "react";
import { useAuthDataContext } from "components/auth";
import { getCustomerId, storeCustomerId } from "components/user-config-storage";
import { getUserContracts, getUserCustomers } from "../../services/commonService";
import { getDeploymentsService } from "../../services/deploymentServices";

const CurrentCustomerContext = createContext(null);

const getDefaultCustomerId = customers => (!!customers?.length ? customers[0]._id : "");

const CurrentCustomerProvider = ({ children }) => {
  const { hasAdminPrivileges, user } = useAuthDataContext();

  const username = user.sub;
  const [customerId, setCustomerId] = useState(getCustomerId(username));

  const [customers, setCustomers] = useState(null);
  const [contracts, setContracts] = useState(null);
  const [deployments, setDeployments] = useState(null);

  const [customersTotal, setCustomersTotal] = useState(null);
  const [contractsTotal, setContractsTotal] = useState(null);
  const [deploymentsTotal, setDeploymentsTotal] = useState(null);

  const [customerContractIds, setCustomerContractIds] = useState(null);
  const [contractIds, setContractIds] = useState(null);

  const customersCallback = response => {
    setCustomers(response.data);
    setCustomersTotal(parseInt(response.headers["pagination-total-results"]));
  };

  const contractsCallback = response => {
    setContracts(response.data);
    setContractsTotal(parseInt(response.headers["pagination-total-results"]));

    const mappedContracts = response.data?.map(item => item._id);
    const customerContracts = response.data?.filter(item => item.customer_id === customerId)?.map(item => item._id);

    setContractIds(mappedContracts);
    setCustomerContractIds(user.scopedViewByCustomer ? customerContracts : mappedContracts);
  };

  const getCustomers = () => {
    getUserCustomers(
      {
        username,
      },
      customersCallback,
    );
  };

  const getContracts = () => {
    getUserContracts(
      {
        username,
      },
      contractsCallback,
    );
  };

  const deploymentCallback = response => {
    setDeployments(response.data);
    setDeploymentsTotal(parseInt(response.headers["pagination-total-results"]));
  };

  const getDeployments = () => {
    getDeploymentsService({}, deploymentCallback);
  };

  useEffect(() => {
    getCustomers();
    getContracts();
    getDeployments();
  }, []);

  useEffect(
    () => {
      if (hasAdminPrivileges && customers && !customerId) {
        setCustomerId(getDefaultCustomerId(customers));
      } else if (user.scopedViewByCustomer && !customerId) {
        setCustomerId(getDefaultCustomerId(customers));
      }
    },
    [customers, customerId, hasAdminPrivileges, user],
  );

  useEffect(
    () => {
      // Stores the new customer selected in the user config
      if (customerId) {
        storeCustomerId(username, customerId);
      }
    },
    [customerId, username],
  );

  return (
    <CurrentCustomerContext.Provider
      value={{
        currentCustomerId: customerId,
        setCurrentCustomerId: setCustomerId,
        customers,
        customersTotal,
        contracts,
        contractsTotal,
        deployments,
        deploymentsTotal,
        customerContractIds,
        contractIds,
        getCustomers,
        getContracts,
        getDeployments,
      }}
    >
      {children}
    </CurrentCustomerContext.Provider>
  );
};

const useCurrentCustomerContext = () => {
  const context = useContext(CurrentCustomerContext);
  if (context === undefined) {
    throw new Error("useCurrentCustomerContext must be used within a CurrentCustomerProvider");
  }
  return context;
};

export { CurrentCustomerProvider, useCurrentCustomerContext };
